import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Loader2, Upload, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { rcApi, errMsg, fmt, today, TYPE_AR, STATUS_AR } from "./ridercash/rcApi";

const DepositForm = ({ onDone }) => {
  const [f, setF] = useState({ amount: "", date: today(), note: "", file: null });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!(Number(f.amount) > 0) || !f.file) return toast.error("أدخل المبلغ وأرفق صورة الإيصال");
    const fd = new FormData();
    fd.append("amount", f.amount); fd.append("date", f.date); fd.append("note", f.note); fd.append("file", f.file);
    setBusy(true);
    try { await rcApi.portalDeposit(fd); toast.success("تم إرسال الإيصال وبانتظار التأكيد"); setF({ amount: "", date: today(), note: "", file: null }); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-2xl border bg-white p-4" data-testid="portal-rc-form">
      <p className="font-semibold">رفع إيصال توريد</p>
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="portal-rc-amount" type="number" placeholder="المبلغ المورّد" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <Input data-testid="portal-rc-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
      </div>
      <Input data-testid="portal-rc-note" placeholder="ملاحظة (اختياري)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed p-3 text-sm text-slate-600" data-testid="portal-rc-file-label">
        <Upload className="h-4 w-4" />{f.file ? f.file.name : "صورة أو PDF للإيصال"}
        <input type="file" accept="image/*,application/pdf" hidden data-testid="portal-rc-file" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} />
      </label>
      <Button className="w-full" data-testid="portal-rc-submit" disabled={busy} onClick={send}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}إرسال</Button>
    </div>
  );
};

export const PortalRiderCash = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => rcApi.portal().then(setD).catch(() => setD({ balance: 0, txns: [] })), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />;
  const over = d.limit > 0 && d.balance > d.limit;
  return (
    <div className="space-y-3" dir="rtl" data-testid="portal-rider-cash">
      <div className={`rounded-2xl p-5 text-white ${over ? "bg-rose-600" : "bg-emerald-700"}`}>
        <div className="flex items-center gap-2 text-sm opacity-90"><Banknote className="h-4 w-4" />النقد المستحق عليك للتوريد</div>
        <p className="mt-1 text-3xl font-bold tabular-nums" data-testid="portal-rc-balance">{fmt(d.balance)} <span className="text-base">ريال</span></p>
        <p className="mt-1 text-xs opacity-90">بانتظار التأكيد: {fmt(d.pending)} · الحد المسموح: {fmt(d.limit)}</p>
        {over && <p className="mt-2 flex items-center gap-1 text-xs font-semibold" data-testid="portal-rc-over"><AlertTriangle className="h-4 w-4" />تجاوزت الحد المسموح — يرجى التوريد فوراً</p>}
      </div>
      <DepositForm onDone={load} />
      <div className="rounded-2xl border bg-white p-4">
        <p className="mb-2 font-semibold">سجل الحركات</p>
        {d.txns.map((t) => (
          <div key={t.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0" data-testid={`portal-rc-txn-${t.id}`}>
            <div><p>{TYPE_AR[t.type]} <span className="text-xs text-slate-400">{STATUS_AR[t.status]}</span></p><p className="text-xs text-slate-500">{t.date}{t.reject_reason ? ` · ${t.reject_reason}` : ""}</p></div>
            <p className={`font-semibold tabular-nums ${t.type === "collect" ? "text-rose-600" : "text-emerald-600"}`}>{t.type === "collect" ? "+" : "−"}{fmt(t.amount)}</p>
          </div>
        ))}
        {!d.txns.length && <p className="py-4 text-center text-sm text-slate-400">لا توجد حركات</p>}
      </div>
    </div>
  );
};
